import { useState, useEffect } from 'react';
import { Image, FileText, Video, Music, Archive, File } from 'lucide-react';
import { getFileTypeCategory } from '../utils/fileTypeUtils';
import { apiService } from '../services/api';
import type { TicketAttachmentResponse } from '../types/tickets';

interface AttachmentThumbnailProps {
  attachment: TicketAttachmentResponse;
  size?: 'sm' | 'md';
}

const sizeClasses = {
  sm: { container: 'w-6 h-6', icon: 'h-3 w-3' },
  md: { container: 'w-10 h-10', icon: 'h-5 w-5' },
};

function FileTypeIcon({ contentType, className }: { contentType: string; className: string }) {
  const category = getFileTypeCategory(contentType);
  switch (category) {
    case 'image':
      return <Image className={className} />;
    case 'pdf':
    case 'document':
      return <FileText className={className} />;
    case 'video':
      return <Video className={className} />;
    case 'audio':
      return <Music className={className} />;
    case 'archive':
      return <Archive className={className} />;
    default:
      return <File className={className} />;
  }
}

export function AttachmentThumbnail({ attachment, size = 'md' }: AttachmentThumbnailProps) {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!attachment.thumbnailUrl);
  const [error, setError] = useState(false);
  const classes = sizeClasses[size];

  useEffect(() => {
    if (!attachment.thumbnailUrl) return;

    let cancelled = false;

    async function loadThumbnail() {
      try {
        const blob = await apiService.fetchAttachmentBlob(attachment.thumbnailUrl!);
        if (!cancelled) {
          const url = URL.createObjectURL(blob);
          setThumbnailUrl(url);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    }

    loadThumbnail();

    return () => {
      cancelled = true;
    };
  }, [attachment.thumbnailUrl]);

  // Revoke object URL on unmount or when URL changes
  useEffect(() => {
    return () => {
      if (thumbnailUrl) {
        URL.revokeObjectURL(thumbnailUrl);
      }
    };
  }, [thumbnailUrl]);

  if (loading) {
    return (
      <div
        className={`${classes.container} rounded-lg bg-muted animate-pulse flex-shrink-0`}
      />
    );
  }

  if (thumbnailUrl && !error) {
    return (
      <div className={`${classes.container} rounded-lg overflow-hidden flex-shrink-0`}>
        <img
          src={thumbnailUrl}
          alt={attachment.fileName}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  return (
    <div
      className={`${classes.container} rounded-lg bg-gray-50/50 dark:bg-gray-800/50 flex items-center justify-center flex-shrink-0`}
    >
      <FileTypeIcon
        contentType={attachment.contentType}
        className={`${classes.icon} text-gray-600 dark:text-gray-400`}
      />
    </div>
  );
}
